import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MapHotelContext } from 'contexts/MapHotelContext';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Rating from 'components/ui/Rating/Rating';
import { useTranslation } from 'react-i18next';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { getHotelInfo } from 'services/hotel';
import { HotelInfoResponse } from 'ts/hotel/interfaces/HotelInfoResponse';
import { checkAHAVI, replaceHtmlToStr } from 'utils/utils';
import ShowMoreLess from 'components/shared/ShowMoreLess';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import DetailsMap from '../../Maps/DetailsMap';
import Slider from 'react-slick';
import ImageGallery from 'react-image-gallery';
import { getKey } from 'utils/hotel';

import Skeleton from 'react-loading-skeleton';
import { SerpDetails } from '../../Details/SerpDetails';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface HotelInfoProps { }

const HotelInfo: FC<HotelInfoProps> = () => {
  const { t } = useTranslation();
  const { activeHotel } = useContext(MapHotelContext);
  const { filter } = useContext(HotelFilterContext);
  const { hotelIndex } = useContext(HotelConfigContext);
  const [hotelInfo, setHotelInfo] = useState<HotelInfoResponse>();
  const [resetMap, setResetMap] = useState(false);
  const activeHotelKey = useRef('');

  useEffect(() => {
    if (!activeHotel) return;
    if (activeHotel.htuKey !== activeHotelKey.current) {
      setHotelInfo(undefined);
      setResetMap(true);

      setTimeout(() => {
        setResetMap(false);
      }, 100);

      (async () => {
        const expediaRoom = activeHotel?.roomClasses.find(
          (room) => room.suppDesc === 'Expedia' || room.suppDesc === 'Expedia Package Rate'
        );

        const htuKey = expediaRoom?.htuKey || activeHotel?.htuKey;

        const bToken =
          expediaRoom?.hotelRooms[0].bToken || activeHotel?.roomClasses[0].hotelRooms[0].bToken;

        const hotelInfo = await getHotelInfo(
          {
            hotelUkey: htuKey,
            searchToken: sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex))!,
            roomBToken: bToken,
          },
          hotelIndex
        );

        setHotelInfo(hotelInfo?.hotelDetails);
      })();
      activeHotelKey.current = activeHotel.htuKey;
    }
  }, [activeHotel]); // eslint-disable-line

  const onClickAccordion = (e: any) => {
    if (e.target.classList.contains('accordion')) {
      e.target.classList.toggle('active');
      const panel = e.target.nextElementSibling;
      if (panel.style.maxHeight) {
        panel.style.maxHeight = null;
      } else {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }
    }
  };
  return (
    <div className="hotel-result-settings-content">
      <div
        className="details-wrapper hotel-details-wrapper"
        style={{ paddingBlock: 0, alignItems: 'stretch' }}
      >
        <div className="details-summary" style={{ padding: 0, minHeight: '100%' }}>
          {!!hotelInfo && (
            <Fragment>
              <div
                className="default-shadow details-block"
                style={{ flex: 1, minHeight: 'calc(100% - 20px)' }}
              >
                <div className="title-underline text-lg mb-20 title-header-grey">
                  {t('hotel.details')}
                </div>
                <div style={{ maxHeight: 1350, overflowY: 'auto' }}>
                  <div className="mb-20 text-lh">
                    <div className="text-bold">{t('search.check_in')}</div>
                    <div
                      dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelInfo.checkIn) }}
                    ></div>
                  </div>
                  <div className="mb-20 text-lh">
                    <div className="text-bold">{t('search.check_out')}</div>
                    <div
                      dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelInfo.checkOut) }}
                    ></div>
                  </div>
                  <div className="pb-20">
                    {hotelInfo.hotelFacilities && (
                      <div className="text-lh">
                        <div className="title-underline text-lg mb-20">{t('hotel.facilities')}</div>
                        <div id="text-fac">
                          {hotelInfo.hotelFacilities.map((facility, index) => (
                            <div key={index}>{facility}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          )}
          {!hotelInfo && (
            <div
              className="default-shadow details-block"
              style={{ flex: 1, minHeight: 'calc(100% - 20px)' }}
            >
              <div className="title-underline title-header-grey text-lg mb-20">
                <Skeleton height={20} width={100} />
              </div>
              {Array(30)
                .fill(0)
                .map((_, index) => (
                  <div className="mb-5" key={index}>
                    <Skeleton height={16} width={'100%'} />
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="details flex-column">
          {!!activeHotel && (
            <div className="default-shadow details-block">
              <div>
                <div className="title mb-3 title-header-grey">{activeHotel?.item.name}</div>
                <Rating className="stars mb-10" value={activeHotel.item.star} color="#4b71a3" />
                <div className="mb-5">{activeHotel.addressInfo.address}</div>
                <div className="location-from-text mb-20">
                  <span className="text-bold">
                    {activeHotel.distance}
                    {t('km')}
                  </span>
                  <span> {t('from')} </span>
                  <span className="text-bold">
                    {filter?.location.value.name || activeHotel.position.distanceText}
                  </span>
                </div>
              </div>
              <div className="details-slider-images">
                <ImageGallery
                  items={[
                    {
                      original: activeHotel.info.picture,
                      thumbnail: activeHotel.info.picture,
                      originalAlt: 'pic',
                      thumbnailAlt: 'pic',
                    },
                    ...(hotelInfo?.images?.slice(0, 24).map((img) => ({
                      original: img.link,
                      thumbnail: img.link,
                      originalAlt: img.alt,
                      thumbnailAlt: img.alt,
                    })) || []),
                  ]}
                  showThumbnails={true}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  thumbnailPosition="right"
                />
              </div>
            </div>
          )}

          {hotelInfo?.description && (
            <div className="default-shadow details-block" id="text-desc" style={{ flex: 1 }}>
              <div className="title-underline title-header-grey text-lg mb-20">
                {t('hotel.overview')}
              </div>
              <div
                className={'text-lh' + (checkAHAVI(hotelInfo.description) ? ' rtl' : ' ltr')}
                dangerouslySetInnerHTML={{ __html: hotelInfo.description }}
              ></div>

              <ShowMoreLess selector="#text-desc .text-lh" maxHeight={320} />
            </div>
          )}

          {!!hotelInfo?.listOfRoomInfo?.[0] && (
            <div className="default-shadow details-block">
              <div className="title-underline title-header-grey text-lg mb-20">
                {t('hotel.room_info')}
              </div>

              <div style={{ height: 300, overflowY: 'auto' }}>
                {hotelInfo.listOfRoomInfo.map((room, index) => (
                  <div key={index} onClick={onClickAccordion}>
                    <div className="accordion">{room.shortDesc}</div>
                    <div className="panel">
                      {room.listOfImageURL?.some((img) => img.includes('_z.jpg')) && (
                        <div className="hotel-result-settings-img mt-20" style={{ maxWidth: 350 }}>
                          <Slider arrows>
                            {room.listOfImageURL
                              .filter((img) => img.includes('_z.jpg'))
                              .map((img, subindex) => (
                                <div className="hotel-result-settings-slide" key={subindex}>
                                  <img src={img} alt={'room'} />
                                </div>
                              ))}
                          </Slider>
                        </div>
                      )}

                      {!!room.desc.length && (
                        <div className="mb-20 text-lh">
                          <div className="title text-md">{t('hotel.about_room')}</div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: replaceHtmlToStr(room.desc),
                            }}
                          ></div>
                        </div>
                      )}

                      {!!room.listOfAmenity?.length && (
                        <div className="row mb-20">
                          <div className="col-12 title text-md">{t('hotel.room_facilities')}</div>
                          {room.listOfAmenity.map((amenity, index) => (
                            <div className="col-md-6 col-12 mb-5" key={index}>
                              <Checkbox label={amenity.desc} disabled checked={true} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="details-wrapper hotel-details-wrapper" style={{ paddingTop: 0 }}>
        <div className="details-summary" style={{ padding: 0 }}>
          {!resetMap && !!activeHotel && (
            <div className="details-block default-shadow hotel-info-loc">
              <div className="title-underline title-header-grey text-lg mb-20">
                {t('hotel.location')}
              </div>
              <div className="w100p h100p">
                <DetailsMap
                  lat={activeHotel.position?.latitude}
                  lng={activeHotel.position?.longitude}
                  zoom={11.5}
                />
              </div>
            </div>
          )}
        </div>
        <div className="details">
          {!!activeHotel && <SerpDetails hotel={activeHotel} onLoad={() => { }} />}
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(HotelInfo, 'HotelInfo');
